import React, { useMemo, useState } from 'react';
import { Toggle } from '@fluentui/react';

import { fmt } from '../ContractStatistics.Functions';
import * as Functions from './BillingControl.Functions';

interface IProps {
    matrix: Functions.BillingMatrix;
    currency: string;
}

/** Cuánto pesa el casillero respecto del más grande, en cuatro escalones de color. */
const heatLevel = (amount: number, max: number): string => {
    if (amount <= 0 || max <= 0) return '';
    const ratio = amount / max;
    if (ratio > 0.6) return ' bc-matrix__cell--l4';
    if (ratio > 0.3) return ' bc-matrix__cell--l2';
    if (ratio > 0.1) return ' bc-matrix__cell--l1';
    return ' bc-matrix__cell--l1';
};

/**
 * Qué se cobra dónde: la matriz de conceptos del contrato contra las visitas.
 *
 * Es la traducción literal del anexo económico contra la realidad. Cada fila es un
 * concepto pactado —aunque nunca se haya usado—, cada columna una visita del
 * contrato. Un casillero vacío puede ser normal; una fila entera vacía o un cobro
 * sin concepto pactado, no.
 */
export const BillingMatrixComponent: React.FC<IProps> = ({ matrix, currency }) => {
    const [showCounts, setShowCounts] = useState<boolean>(false);

    const maxAmount = useMemo(() => {
        let max = 0;
        matrix.rows.forEach((row) => row.cells.forEach((cell) => {
            if (cell.amount > max) max = cell.amount;
        }));
        return max;
    }, [matrix]);

    const never = matrix.rows.filter((row) => row.isContracted && row.total.count === 0).length;
    const orphan = matrix.rows.find((row) => !row.isContracted && row.key.startsWith('__'));

    return (
        <div className="cs-card">
            <div className="cs-card__header">
                <div className="cs-card__title">Qué se cobra dónde</div>
                <div className="cs-card__desc">
                    Conceptos del contrato contra visitas. El color indica cuánta plata mueve cada casillero. Los
                    conceptos sin ningún cobro quedan igual en la tabla: son los que hay que revisar.
                </div>
            </div>

            <div className="cs-card__toolbar">
                <Toggle
                    inlineLabel
                    label="Ver cantidades en vez de importes"
                    checked={showCounts}
                    onChange={(_event, checked) => setShowCounts(!!checked)}
                />
                <span className="cs-kpi-cell__desc">
                    {matrix.rows.length} conceptos · {matrix.columns.length} visitas
                    {never > 0 ? ` · ${never} sin cobrar nunca` : ''}
                </span>
            </div>

            {matrix.rows.length === 0 ? (
                <div className="cs-empty cs-empty--compact">
                    <span>El contrato no tiene conceptos cargados ni consumos en el período.</span>
                </div>
            ) : (
                <div className="cs-card__body">
                    <div className="bc-matrix">
                        <table className="bc-matrix__table">
                            <thead>
                                <tr>
                                    <th className="bc-matrix__corner">Concepto</th>
                                    {matrix.columns.map((column) => (
                                        <th key={column.key} title={column.label}>
                                            {column.label}
                                            {column.isOptional && <span className="ve-matrix__opt-tag">opc.</span>}
                                        </th>
                                    ))}
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matrix.rows.map((row) => {
                                    const isNever = row.isContracted && row.total.count === 0;
                                    const isOrphan = !row.isContracted;
                                    return (
                                        <tr
                                            key={row.key}
                                            className={
                                                (isNever ? 'bc-matrix__row--never' : '') +
                                                (isOrphan ? ' bc-matrix__row--orphan' : '')
                                            }
                                        >
                                            <td className="bc-matrix__concept" title={row.label}>
                                                {row.label}
                                                {/* Dos casos distintos, y conviene no confundirlos: el cobro sin
                                                    concepto alguno, y el cobro valorizado con un concepto que no
                                                    pertenece a este contrato. */}
                                                {isOrphan && !!row.conceptId && (
                                                    <>
                                                        {' '}
                                                        <span
                                                            className="fp-tag fp-tag--bad"
                                                            title="El consumo apunta a un concepto que no figura entre los conceptos de este contrato: puede ser de otra adenda, uno borrado o uno de una versión anterior."
                                                        >
                                                            fuera de este contrato
                                                        </span>
                                                    </>
                                                )}
                                                {isNever && <> <span className="fp-tag fp-tag--warn">nunca cobrado</span></>}
                                                {row.isContracted && row.isApproved === false && (
                                                    <> <span className="fp-tag fp-tag--warn">sin revisar</span></>
                                                )}
                                            </td>
                                            {matrix.columns.map((column) => {
                                                const cell = row.cells.get(column.key);
                                                if (!cell) {
                                                    return (
                                                        <td key={column.key} className="bc-matrix__empty" title="Sin cobros">
                                                            ·
                                                        </td>
                                                    );
                                                }
                                                return (
                                                    <td
                                                        key={column.key}
                                                        className={'bc-matrix__cell' + heatLevel(cell.amount, maxAmount)}
                                                        title={`${row.label} · ${column.label}: ${fmt(cell.amount, currency)} en ${cell.count} consumos de ${cell.patients} pacientes`}
                                                    >
                                                        {showCounts ? cell.count : fmt(cell.amount, currency)}
                                                        <span className="bc-matrix__count">
                                                            {showCounts
                                                                ? `${cell.patients} pac.`
                                                                : `${cell.count} × ${cell.patients} pac.`}
                                                        </span>
                                                    </td>
                                                );
                                            })}
                                            <td className="bc-matrix__cell">
                                                <strong>{showCounts ? row.total.count : fmt(row.total.amount, currency)}</strong>
                                            </td>
                                        </tr>
                                    );
                                })}
                                <tr className="bc-matrix__total">
                                    <td className="bc-matrix__concept">Total</td>
                                    {matrix.columns.map((column) => (
                                        <td key={column.key} className="bc-matrix__cell">
                                            {showCounts ? column.total.count : fmt(column.total.amount, currency)}
                                        </td>
                                    ))}
                                    <td className="bc-matrix__cell">
                                        {showCounts ? matrix.total.count : fmt(matrix.total.amount, currency)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="bc-matrix__legend">
                        <span><span className="bc-matrix__swatch" style={{ background: 'color-mix(in srgb, var(--ichtys-primary) 38%, var(--ichtys-surface))' }} />Mueve más plata</span>
                        <span><span className="bc-matrix__swatch" style={{ background: 'color-mix(in srgb, var(--ichtys-primary) 6%, var(--ichtys-surface))' }} />Mueve poca</span>
                        <span><span className="bc-matrix__swatch" style={{ background: 'var(--ichtys-surface)' }} />· Sin cobros en esa visita</span>
                        {orphan && (
                            <span>
                                <strong>{fmt(orphan.total.amount, currency)}</strong> se cobró sin ningún concepto
                                asociado: no hay precio del contrato que aplicarle
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BillingMatrixComponent;
